<template>
  <card v-if="!ratePlan.useSimpleRelativeRateAdjustment"
        :title="roomType.name[hotelSettings.details.primaryLanguage]"
        :style="`--season-color: rgba(${toRGB(season.rgbColorCode)}, 0.35);`"
        :collapsed="!expanded"
        card-type="collapsible"
        cols="12"
        header-style-class="card__header--season-color"
        @collapse="onCollapse">
    <hm-card-inner>
      <hm-input-rate v-model="internalValue"
                     :disabled="disabled"
                     :lazy="true"
                     :name="`${season.id}_${roomType.id}_default`"
                     :percentage="false"
                     :relative="ratePlan.ratePlanMode === 'Derived'"
                     :absolute="ratePlan.ratePlanMode === 'Regular'"
                     :rate-mode="true"
                     :rate-mode-disabled="ratePlan.ratePlanMode === 'Derived'"
                     :label="$t('seasonRateEditor.roomTypeSeasonRateEditor.defaultPrice')" />
    </hm-card-inner>
    <hm-card-inner v-if="roomType.occupancyBasedRates && expanded" :title="$t('seasonRateEditor.roomTypeSeasonRateEditor.card.occupancyBasedRates')">
      <hm-col :grid="true" cols="12">
        <hm-col cols="7">
          <template v-for="numPersons in intArray(roomType.maxOccupancy - roomType.minOccupancy + 1, roomType.minOccupancy)">
            <hm-input-rate v-model="internalValue.personRates[numPersons]"
                           :disabled="disabled"
                           :key="numPersons"
                           :label="$t('seasonRateEditor.roomTypeSeasonRateEditor.personRates', { numPersons: numPersons })"
                           :help="getPersonRateTitle(numPersons)"
                           :name="`${season.id}_${roomType.id}_${numPersons}`"
                           :lazy="true"
                           :relative="true"
                           :absolute="ratePlan.ratePlanMode !== 'Derived'"
                           cols="12"
            />
          </template>
        </hm-col>
        <rate-editor-caculated-rates :room-type="roomType" :calculated-rates="calculatedRates" />
      </hm-col>
    </hm-card-inner>
    <hm-card-inner v-if="hasLicense('ChildRates') && expanded" :title="$t('seasonRateEditor.roomTypeSeasonRateEditor.card.childRates')">
      <template v-if="internalValue.rateMode !== 'PerPerson'">
        <hm-col cols="12">
          <span class="info-text info-text--full-size info-text--no-margin col-12" v-html="$t('seasonRateEditor.roomTypeSeasonRateEditor.infoTextChildRatesPerRoom')"></span>
        </hm-col>
      </template>
      <template v-else>
        <hm-input-number v-model="internalValue.minAdultsForChildRate"
                         :validate="`max_value:${roomType.maxOccupancy}|min_value:0`"
                         :disabled="disabled || internalValue.rateMode !== 'PerPerson'"
                         :lazy="true"
                         :label="$t('seasonRateEditor.roomTypeSeasonRateEditor.minAdultsForChildRate')"
                         :help="$t('seasonRateEditor.roomTypeSeasonRateEditor.minAdultsForChildRate_help')"
                         name="minAdultsForChildRate" />
        <template v-if="!ratePlan.useRatePlanLevelChildRates">
          <template v-for="ageCategory in hotelSettings.ageCategories.all()" v-if="!ageCategory.deleted && ageCategory.ageFrom >= 0">
            <hm-col :key="ageCategory.id" cols="12">
              <hm-input-rate v-model="internalValue.childRates[ageCategory.id]"
                             :disabled="disabled || internalValue.rateMode !== 'PerPerson' || internalValue.minAdultsForChildRate >= roomType.maxOccupancy"
                             :label="ageCategory.name[hotelSettings.details.primaryLanguage]"
                             :name="`${season.id}_${roomType.id}_${ageCategory.id}`"
                             :lazy="true"
                             :relative="true"
                             :absolute="true" />
            </hm-col>
          </template>
        </template>
      </template>
    </hm-card-inner>
  </card>
</template>

<script>
import HotelmeisterMixin from '../../../mixins/HotelmeisterMixin';
import SaveWatchMixin from '../../../mixins/SaveWatchMixin';
import { isEmpty } from 'lodash';
import HmCardInner from '../layout/HmCardInner';
import HmInputMoney from '../inputs/HmInputMoney';
import HmInputNumber from '../inputs/HmInputNumber';
import RateEditorCaculatedRates from './RateEditorCaculatedRates';

export default {
  name: 'RoomTypeSeasonRateEditor',
  components: { RateEditorCaculatedRates, HmInputNumber, HmInputMoney, HmCardInner },
  mixins: [HotelmeisterMixin, SaveWatchMixin],

  autosave: [
    {
      name: 'internalValue',
      variable: 'internalValue',
      fn: function (cbSaveComplete) {
        if (this.disabled !== true) {
          // this.$emit('input', this.internalValue);
          this.axios.put(this.serviceUrls.seasonRate.updateRoomTypeRatePlanSeasonDetail(this, this.ratePlan.id), this.internalValue)
            .then((response) => {
              cbSaveComplete();
              // this.calculatedRates = response.data.data.byOccupancy;
              // need to do it this way, because overwriting the whole thing causes the parent componet to re-render and the lazy input to cause issues if one input is currently being edited
              Object.keys(response.data.data.byOccupancy).forEach((key) => { this.calculatedRates[key] = response.data.data.byOccupancy[key]; });
            })
            .catch((error) => {
              this.showMessages(error.response);
              cbSaveComplete();
              this.calculateRates();
            });
        } else {
          cbSaveComplete();
        }
      },
      exclude: ['relative', 'percentage'],
    },
  ],

  props: {
    season: {
      required: true,
      type: Object,
    },
    ratePlan: {
      required: true,
      type: Object,
    },
    roomType: {
      required: true,
      type: Object,
    },
    value: {
      required: true,
    },
    disabled: {
      required: false,
      type: Boolean,
      default: false,
    },
    expanded: {
      required: true,
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      internalValue: this.value,
      calculatedRates: {},
    };
  },


  watch: {
    value: function (newVal) {
      this.internalValue = this.value;
    },
    internalValue: function (newVal) {
      if (this.disabled !== true) {
        this.calculateRates();
        // this.$emit('input', this.internalValue);
      }
    },
  },

  created() {
    this.internalValue = this.value;

    for (let i = this.roomType.minOccupancy; i <= this.roomType.maxOccupancy; i++) {
      this.$set(this.calculateRates, `${i}`, null);
    }
  },

  methods: {
    onCollapse() {
      this.$emit('collapse', this.roomType.id);
    },
    toRGB(hexColorCode) {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexColorCode);
      return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '';
    },
    calculateRates() {
      this.axios.post(this.serviceUrls.seasonRate.calculate(this, this.ratePlan.id, true), {
        roomTypeId: this.roomType.id,
        seasonId: this.season.id,
        numAdults: 1,
      })
        .then((response) => {
          this.calculatedRates = response.data.data.byOccupancy;
        });
    },
    getPersonRateTitle(numPersons) {
      if (this.internalValue.rateMode === 'PerRoom') {
        return this.$t('seasonRateEditor.roomTypeSeasonRateEditor.personRates_help_roomRate', { numPersons: numPersons });
      } else if (this.internalValue.rateMode === 'PerPerson') {
        if (numPersons <= this.roomType.expectedOccupancy) {
          if (numPersons > 1) {
            return this.$t('seasonRateEditor.roomTypeSeasonRateEditor.personRates_help_person1toX', { numPersons: numPersons });
          } else {
            return this.$t('seasonRateEditor.roomTypeSeasonRateEditor.personRates_help_xPersons', { numPersons: numPersons });
          }
        } else if (numPersons === (this.roomType.expectedOccupancy + 1)) {
          return this.$t('seasonRateEditor.roomTypeSeasonRateEditor.personRates_help_expectedPlus1Persons', { numPersons: numPersons });
        } else {
          return this.$t('seasonRateEditor.roomTypeSeasonRateEditor.personRates_help_expectedPlusNPersons', { numPersons: numPersons, expectedOccupancyPlus1: this.roomType.expectedOccupancy + 1 });
        }
      } else {
        return this.$t('seasonRateEditor.roomTypeSeasonRateEditor.personRates_help_unsupportedRateMode');
      }
    },
  },
};
</script>

<style scoped lang="scss">

</style>

<style lang="scss">
</style>



// WEBPACK FOOTER //
// src/components/lib/seasonRateEditor/RoomTypeSeasonRateEditor.vue